import {
  Router,
  CanMatchFn,
  CanActivateFn,
  ActivatedRoute,
  CanActivateChildFn,
  RouterStateSnapshot,
  ActivatedRouteSnapshot,
  UrlTree
} from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { inject } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { LoginResult } from 'src/app/models/login/login-result.model';
import { NotificationsService } from 'src/app/services/notifications/notifications.service';

const DEFAULT_PRODUCT_KEY = 'generals_defaultProduct';

// Guards
export const canMatchAuthGuard: CanMatchFn = (route, segments) => {
  const authService = inject(AuthService);
  const activatedRouteSnapshot = inject(ActivatedRoute).snapshot;

  if (authService.isUserLogged()) { return true; }

  const refreshToken = getAuthTokenQueryStringParam(activatedRouteSnapshot);

  if (refreshToken) {
    return authService.regenerateToken(refreshToken).pipe(
      map(loginResult => {
        updateUserFromLoginResult(refreshToken, loginResult, authService);
        return true;
      }),
      catchError(() => of(false))
    );
  }
  return true;

};

export const canActivateAuthGuard: CanActivateFn = async (
  route,
  state
): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const notificationsService = inject(NotificationsService);

  const HAS_TOKEN = hasAuthTokenInQuery(route);
  const IS_LOGGED = !!authService.isUserLogged();

  if (!HAS_TOKEN && !IS_LOGGED) {
    return handleUnauthenticatedUser(router, state) || false;
  }

  if (HAS_TOKEN || !IS_LOGGED) {
    try {
      const result = await firstValueFrom(isUserLoggedIn(route, router, authService, notificationsService));

      if (result === false) {
        return handleUnauthenticatedUser(router, state);
      }

      return result;
    } catch {
      return handleUnauthenticatedUser(router, state);
    }
  }

  if (!route.data || Object.keys(route.data).length === 0) { return true; }

  return validateAuthorizationWithPermission(route, router, authService, notificationsService);
};

export const canActivateChildAuthGuard: CanActivateChildFn = (
  childRoute,
  state
) => {
  return canActivateAuthGuard(childRoute, state);
};

// Functions
const isUserLoggedIn = (
  route: ActivatedRouteSnapshot,
  router: Router,
  authService: AuthService,
  notificationsService: NotificationsService
) => {

  let refreshToken = getAuthTokenQueryStringParam(route);

  if (!refreshToken && !authService.isUserLogged()) { return of(false); };

  return authService.regenerateToken(refreshToken).pipe(
    map((loginResult) => {
      updateUserFromLoginResult(refreshToken, loginResult, authService);

      if (!route.data || Object.keys(route.data).length === 0) {
        return validateProductAndReturn(route, router, authService);
      }

      return validateAuthorizationWithPermission(
        route,
        router,
        authService,
        notificationsService
      );
    }),
    catchError(() => of(false))
  );
};

const validateAuthorizationWithPermission = (
  route: ActivatedRouteSnapshot,
  router: Router,
  authService: AuthService,
  notificationsService: NotificationsService
): boolean | UrlTree => {
  const data = route.data;
  const feature = data['feature'];
  const roles = data['roles'] || [];

  const isAuthorizedFor = (data.permissions?.length > 0)
    ? authService.isAuthorizedForAny(data.permissions, feature, roles)
    : authService.isAuthorizedFor(data['permission'], feature, roles);


  if (!isAuthorizedFor) {
    return handleUnauthorizedRedirect(
      router,
      notificationsService,
      authService, route
    );
  }

  return validateProductAndReturn(route, router, authService);
};

const handleUnauthorizedRedirect = (
  router: Router,
  notificationsService: NotificationsService,
  authService: AuthService,
  route: ActivatedRouteSnapshot
): boolean | UrlTree => {
  notificationsService.alertNotification(
    'general.Warning',
    'general.UnauthorizedRequest'
  );
  if (!authService.isUserLogged()) {
    return router.createUrlTree(['/login']);
  }

  const urlSegments = route.pathFromRoot
    ?.flatMap(route => route.url)
    ?.map(segment => segment.path);

  if (urlSegments?.length > 1) {
    const parentPath = urlSegments.slice(0, -1);
    return router.createUrlTree(['/', ...parentPath]);
  }

  return router.createUrlTree(['/start']);
};

const validateProductAndReturn = (
  route: ActivatedRouteSnapshot,
  router: Router,
  authService: AuthService
): boolean | UrlTree => {
  const user = authService.currentUser;
  if (!user) { return true; }

  const foundPreference = user.Preferences?.find(p => p.PreferenceKey === DEFAULT_PRODUCT_KEY);
  const targetPath = foundPreference?.PreferenceValue ?? 'start';

  if (hasAuthTokenInQuery(route)) {
    cleanUrlFromToken();

    if (route.routeConfig?.path !== targetPath) {
      return router.createUrlTree([targetPath]);
    }
  }

  return true;
};

const handleUnauthenticatedUser = (
  router: Router,
  state?: RouterStateSnapshot
): UrlTree | boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const origin = urlParams.get('origin');

  if (origin) {
    let returnUrl = new URL(window.location.href);
    returnUrl.searchParams.delete('origin');
    window.location.href = `${decodeURIComponent(origin)}&returnUrl=${encodeURIComponent(returnUrl.toString())}`;
    return true;
  }

  return router.createUrlTree(['/login'], {
    queryParams: state?.url ? { returnUrl: state.url } : {}
  });
};

const cleanUrlFromToken = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete('refresh-token');
  const newUrl = url.toString();
  try {
    window.history.replaceState(null, document.title, newUrl);
  } catch {
    window.location.href = newUrl;
  }
};

const getAuthTokenQueryStringParam = (
  route: ActivatedRouteSnapshot
): string | null => {
  const authTokenFromRoute = route.queryParams['refresh-token'];

  const authTokenFromURL = new URLSearchParams(window.location.search).get(
    'refresh-token'
  );

  return authTokenFromRoute || authTokenFromURL;
};

const updateUserFromLoginResult = (
  refreshToken: string,
  loginResult: LoginResult,
  authService: AuthService
): void => {
  authService.currentUser = {
    ...loginResult,
    RefreshToken: refreshToken || loginResult.RefreshToken
  };
};

const hasAuthTokenInQuery = (snapshot: ActivatedRouteSnapshot): boolean => {
  return Boolean(getAuthTokenQueryStringParam(snapshot));
};
